/**
 * Validation Constants
 * Centralized validation rules and ranges for prospect data
 */

export const VALIDATION_RANGES = {
  height: { minFeet: 4, maxFeet: 7, minInches: 0, maxInches: 11 },
  weight: { min: 100, max: 400 },
  gpa: { min: 0, max: 5.0 },
  gradYear: { pastYears: 2, futureYears: 10 },
} as const;

/**
 * Valid values for Bats and Throws fields
 */
export const BATS_THROWS_VALUES = {
  /** Valid single-character Bats values */
  BATS: ['R', 'L', 'S'] as const,
  /** Valid single-character Throws values */
  THROWS: ['R', 'L'] as const,
  /** Pattern to detect combined Bats/Throws values like "R/R" or "L/R" */
  COMBINED_PATTERN: /^[RLSB][\s/\-\\][RLSB]$/i,
} as const;

/**
 * Check if a value is a combined Bats/Throws value (e.g., "R/R", "L/R")
 */
export function isCombinedBatsThrows(value: string | null | undefined): boolean {
  if (!value) return false;
  return BATS_THROWS_VALUES.COMBINED_PATTERN.test(value.trim());
}

/**
 * Check if a Bats value is valid (single character: R, L, or S)
 */
export function isValidBats(value: string | null | undefined): boolean {
  if (!value) return true; // Empty is valid
  const upper = value.trim().toUpperCase();
  return BATS_THROWS_VALUES.BATS.includes(upper as 'R' | 'L' | 'S');
}

/**
 * Check if a Throws value is valid (single character: R or L)
 */
export function isValidThrows(value: string | null | undefined): boolean {
  if (!value) return true; // Empty is valid
  const upper = value.trim().toUpperCase();
  return BATS_THROWS_VALUES.THROWS.includes(upper as 'R' | 'L');
}

export const FIELD_LIMITS = {
  positionMaxLength: 20,
  phoneDigits: { standard: 10, withCountry: 11 },
} as const;

export function getValidGradYearRange(): { min: number; max: number } {
  const currentYear = new Date().getFullYear();
  return {
    min: currentYear - VALIDATION_RANGES.gradYear.pastYears,
    max: currentYear + VALIDATION_RANGES.gradYear.futureYears,
  };
}
